// utils/userCartWishlistUtils.js
import axios from "axios";
import { showMyAlert } from "../../utils/alertService";

const API_BASE_URL = "https://api.dremerz.net/api";
const USER_CART_KEY = "userCart";
const USER_WISHLIST_KEY = "userWishlist";

// ---- ADDED: sessionStorage wrapper for event dispatch ----
const setSessionValue = (key, value) => {
  sessionStorage.setItem(key, value);
  window.dispatchEvent(new Event("session-storage-change"));
};
// ----------------------------------------------------------

// Helper function to get current user data
const getCurrentUser = () => {
  return JSON.parse(sessionStorage.getItem("user"));
};

// Helper function to update user data in API
const updateUserInAPI = async (updateData) => {
  const user = getCurrentUser();
  if (!user) return false;

  try {
    const response = await axios.put(
      `${API_BASE_URL}/Kalanjiyam_Users_Registration/${user.id}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user in API:", error);
    return false;
  }
};

// Cart Functions
export const cartUtils = {
  getCart: () => {
    const cartData = sessionStorage.getItem(USER_CART_KEY);
    return cartData ? JSON.parse(cartData) : {};
  },

  addToCart: async (productId, quantity = 1, size = "") => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentCart = cartUtils.getCart();

    const existingKey = Object.keys(currentCart).find(key => {
      const item = currentCart[key];
      return item.productId === productId && item.size === size;
    });

    let newCart = { ...currentCart };

    if (existingKey) {
      newCart[existingKey] = {
        ...newCart[existingKey],
        quantity: newCart[existingKey].quantity + quantity
      };
    } else {
      const newKey = `${productId}_${size}_${Date.now()}`;
      newCart[newKey] = {
        productId,
        quantity,
        size
      };
    }

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_CART_KEY, JSON.stringify(newCart));

    try {
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_CART_KEY, JSON.stringify(currentCart));

      showMyAlert({
        type: "danger",
        title: "Error while updating",
        message: "Failed to update cart. Please try again.",
        duration: 2500,
      });
      return false;
    }
  },

  removeFromCart: async (cartItemKey) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentCart = cartUtils.getCart();

    const { [cartItemKey]: _, ...newCart } = currentCart;

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_CART_KEY, JSON.stringify(newCart));

    try {
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_CART_KEY, JSON.stringify(currentCart));

      showMyAlert({
        type: "danger",
        title: "Error while updating",
        message: "Failed to update cart. Please try again.",
        duration: 2500,
      });
      return false;
    }
  },

  updateQuantity: async (cartItemKey, newQuantity) => {
    if (newQuantity < 1) {
      return await cartUtils.removeFromCart(cartItemKey);
    }

    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentCart = cartUtils.getCart();

    const newCart = {
      ...currentCart,
      [cartItemKey]: {
        ...currentCart[cartItemKey],
        quantity: newQuantity
      }
    };

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_CART_KEY, JSON.stringify(newCart));

    try {
      const apiSuccess = await updateUserInAPI({
        cartItems: newCart
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_CART_KEY, JSON.stringify(currentCart));

      showMyAlert({
        type: "danger",
        title: "Error while updating",
        message: "Failed to update cart. Please try again.",
        duration: 2500,
      });
      return false;
    }
  },

  clearCart: async () => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentCart = cartUtils.getCart();

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_CART_KEY, JSON.stringify({}));

    try {
      const apiSuccess = await updateUserInAPI({
        cartItems: {}
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_CART_KEY, JSON.stringify(currentCart));

      console.error("Failed to clear cart. Please try again.");
      return false;
    }
  },

  getProductCount: (productId) => {
    const cart = cartUtils.getCart();
    return Object.values(cart).reduce((total, item) => {
      return item.productId === productId ? total + item.quantity : total;
    }, 0);
  },

  findCartItemKey: (productId, size) => {
    const cart = cartUtils.getCart();
    return Object.keys(cart).find(key => {
      const item = cart[key];
      return item.productId === productId && item.size === size;
    });
  }
};

// Wishlist Functions (unchanged, except setSessionValue applied)
export const wishlistUtils = {
  getWishlist: () => {
    const wishlistData = sessionStorage.getItem(USER_WISHLIST_KEY);
    return wishlistData ? JSON.parse(wishlistData) : [];
  },

  addToWishlist: async (productId) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentWishlist = wishlistUtils.getWishlist();

    if (currentWishlist.includes(productId)) {
      return true;
    }

    const newWishlist = [...currentWishlist, productId];

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_WISHLIST_KEY, JSON.stringify(newWishlist));

    try {
      const apiSuccess = await updateUserInAPI({
        wishListItems: newWishlist
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_WISHLIST_KEY, JSON.stringify(currentWishlist));

      showMyAlert({
        type: "danger",
        title: "Error while updating",
        message: "Failed to update wishlist. Please try again.",
        duration: 2500,
      });
      return false;
    }
  },

  removeFromWishlist: async (productId) => {
    const user = getCurrentUser();
    if (!user) {
      return false;
    }

    const currentWishlist = wishlistUtils.getWishlist();

    const newWishlist = currentWishlist.filter(id => id !== productId);

    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_WISHLIST_KEY, JSON.stringify(newWishlist));

    try {
      const apiSuccess = await updateUserInAPI({
        wishListItems: newWishlist
      });

      if (!apiSuccess) throw new Error("API update failed");

      return true;
    } catch (error) {
      // 🔄 replaced sessionStorage.setItem
      setSessionValue(USER_WISHLIST_KEY, JSON.stringify(currentWishlist));

      showMyAlert({
        type: "danger",
        title: "Error while updating",
        message: "Failed to update wishlist. Please try again.",
        duration: 2500,
      });
      return false;
    }
  },

  isInWishlist: (productId) => {
    return wishlistUtils.getWishlist().includes(productId);
  }
};

// Initialize user data on login
export const initializeUserData = (userData) => {
  if (userData?.cartItems) {
    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_CART_KEY, userData.cartItems);
  } else {
    setSessionValue(USER_CART_KEY, JSON.stringify({}));
  }

  if (userData?.wishListItems) {
    // 🔄 replaced sessionStorage.setItem
    setSessionValue(USER_WISHLIST_KEY, userData.wishListItems);
  } else {
    setSessionValue(USER_WISHLIST_KEY, JSON.stringify([]));
  }
};
